import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Edit2, AlertCircle, MessageSquare } from 'lucide-react'
import { Employee, EmployeeNote } from '../../types'
import { noteService } from '../../services/noteService'
import { toast } from 'sonner'
import { formatDate } from '../../utils/dateUtils'

interface EmployeeNotesModalProps {
  isOpen: boolean
  onClose: () => void
  employee: Employee | null
}

export function EmployeeNotesModal({
  isOpen,
  onClose,
  employee,
}: EmployeeNotesModalProps) {
  const [notes, setNotes] = useState<EmployeeNote[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingNote, setEditingNote] = useState<EmployeeNote | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isImportant: false,
  })

  useEffect(() => {
    if (isOpen && employee) {
      fetchNotes()
    }
  }, [isOpen, employee])

  const fetchNotes = async () => {
    if (!employee) return
    setIsLoading(true)
    try {
      const data = await noteService.getByEmployeeId(employee.id)
      setNotes(data)
    } catch (error) {
      toast.error('Error al cargar las notas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!employee) return

    try {
      if (editingNote) {
        await noteService.update(editingNote.id, formData)
        toast.success('Nota actualizada')
      } else {
        await noteService.create({
          employeeId: employee.id,
          ...formData,
        })
        toast.success('Nota creada')
      }
      setFormData({ title: '', content: '', isImportant: false })
      setShowForm(false)
      setEditingNote(null)
      fetchNotes()
    } catch (error) {
      toast.error('Error al guardar la nota')
    }
  }

  const handleEdit = (note: EmployeeNote) => {
    setFormData({
      title: note.title,
      content: note.content,
      isImportant: note.isImportant,
    })
    setEditingNote(note)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Estas seguro de eliminar esta nota?')) return

    try {
      await noteService.delete(id)
      toast.success('Nota eliminada')
      fetchNotes()
    } catch (error) {
      toast.error('Error al eliminar la nota')
    }
  }


  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[calc(100vh-5rem)] overflow-hidden flex flex-col my-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notas del Empleado</h2>
            {employee && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{employee.fullName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Add Note Button */}
          {!showForm && (
            <button
              onClick={() => {
                setEditingNote(null)
                setFormData({ title: '', content: '', isImportant: false })
                setShowForm(true)
              }}
              className="btn btn-primary mb-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Nota
            </button>
          )}

          {/* Note Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="mb-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Titulo *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contenido *
                  </label>
                  <textarea
                    className="input min-h-[100px]"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    required
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                    checked={formData.isImportant}
                    onChange={(e) =>
                      setFormData({ ...formData, isImportant: e.target.checked })
                    }
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Marcar como importante</span>
                </label>
                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary">
                    {editingNote ? 'Actualizar' : 'Guardar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false)
                      setEditingNote(null)
                    }}
                    className="btn btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Notes List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-200 border-t-primary-600"></div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No hay notas para este empleado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className={`p-4 rounded-lg border ${
                    note.isImportant
                      ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/20'
                      : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-700/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {note.isImportant && (
                          <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                        )}
                        <h3 className="font-medium text-gray-900 dark:text-white">{note.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">
                        {note.content}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Por {note.createdBy.name} - {formatDate(note.createdAt)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(note)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg text-red-500 dark:text-red-400 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
